import React, { useState } from 'react';
import {
  useJobs, useJob, jobIsActive, cancelJob, pauseJob, resumeJob, deleteJob, clearErroredJobs,
} from '../data/firestore';

// Live processes page: every fan-out job with progress, per-task telemetry and
// the kill switches (jobControls). This is the operator's window into the
// Firestore-as-queue system — keep it working as you add job types.

const fmtAgo = (ms) => {
  if (!ms) return '';
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const STATUS_COLOR = {
  queued: 'var(--muted)',
  planning: '#ffd166',
  running: '#4fd1c5',
  stitching: '#4fd1c5',
  done: '#7ce38b',
  error: 'var(--accent, #ff6b6b)',
  cancelled: 'var(--muted)',
};

function TaskRows({ jobId }) {
  const { tasks } = useJob(jobId);
  if (!tasks.length) return <p className="cs-help">No task telemetry yet.</p>;
  return (
    <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
      {tasks.map((t) => (
        <div
          key={t.id}
          style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12, color: 'var(--muted)' }}
        >
          <span style={{ width: 90, color: 'var(--text)' }}>{t.id}</span>
          <span style={{ width: 70, color: STATUS_COLOR[t.status] || 'var(--muted)' }}>{t.status}</span>
          <div className="cs-progress" style={{ flex: 1, height: 5 }}>
            <div style={{ width: `${Math.round((t.status === 'done' ? 1 : t.progress || 0) * 100)}%` }} />
          </div>
          <span style={{ flex: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.message || t.stage || ''}>
            {t.message || t.stage || ''}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Jobs() {
  const jobs = useJobs(100);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);

  if (jobs === null) return <div className="cs-page"><p className="cs-help">Loading…</p></div>;

  const erroredCount = jobs.filter((j) => j.status === 'error').length;

  return (
    <div className="cs-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Jobs</h1>
        <span className="cs-help">
          {jobs.filter(jobIsActive).length} active · {jobs.length} shown
        </span>
        <span style={{ flex: 1 }} />
        {erroredCount > 0 && (
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try { await clearErroredJobs(); } finally { setBusy(false); }
            }}
          >
            Clear {erroredCount} errored
          </button>
        )}
      </div>

      {!jobs.length && (
        <div className="cs-empty" style={{ marginTop: 20 }}>
          No jobs yet. Queue one from an item page (or via <code>queueFanoutJob()</code>).
        </div>
      )}

      <div style={{ display: 'grid', gap: 10, marginTop: 18 }}>
        {jobs.map((j) => {
          const active = jobIsActive(j);
          const pct = Math.round((j.status === 'done' ? 1 : j.progress || 0) * 100);
          const open = openId === j.id;
          return (
            <div key={j.id} className="cs-card" style={{ padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 14 }}>{j.title || j.type || j.id}</strong>
                <span style={{ fontSize: 12, color: STATUS_COLOR[j.status] || 'var(--muted)' }}>
                  {j.paused ? 'paused' : j.status}{j.stage && j.stage !== j.status ? ` · ${j.stage}` : ''}
                </span>
                <span className="cs-help" style={{ fontSize: 12 }}>
                  {j.chunks ? `${j.chunksDone || 0}/${j.chunks} chunks · ` : ''}{fmtAgo(j.createdAt)}
                </span>
                <span style={{ flex: 1 }} />
                {active && !j.paused && (
                  <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => pauseJob(j.id)}>Pause</button>
                )}
                {active && j.paused && (
                  <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => resumeJob(j.id)}>Resume</button>
                )}
                {active && (
                  <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => cancelJob(j.id)}>Cancel</button>
                )}
                {!active && (
                  <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => deleteJob(j.id)}>Remove</button>
                )}
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setOpenId(open ? null : j.id)}>
                  {open ? 'Hide tasks' : 'Tasks'}
                </button>
              </div>
              <div className="cs-progress" style={{ height: 7, marginTop: 8 }}>
                <div style={{ width: `${pct}%` }} />
              </div>
              {j.message && (
                <p className="cs-help" style={{ marginTop: 6, color: j.status === 'error' ? 'var(--accent, #ff6b6b)' : undefined }}>
                  {j.message}
                </p>
              )}
              {j.output?.url && (
                <p className="cs-help" style={{ marginTop: 6 }}>
                  Output: <a href={j.output.url} target="_blank" rel="noreferrer">{j.output.s3Key || j.output.url}</a>
                </p>
              )}
              {open && <TaskRows jobId={j.id} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
